import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Seo from "@/components/Seo";
import { Rise, MaskReveal, Overline } from "@/components/Reveal";

const chapters = [
  { num: "01", year: "c. 1924", title: "A yard begins.", body: "The Drury family sets up trade at Tockenham Corner — a small Wiltshire village a short drive from Swindon. The abattoir opens with a handful of hands and a single stone floor." },
  { num: "02", year: "1958", title: "Incorporated.", body: "F. Drury & Sons Limited is formally registered as a company on the 5th of September, at the same address it holds today. The trade classification: wholesale of meat and meat products." },
  { num: "03", year: "1980s – 90s", title: "Weathering the industry.", body: "BSE, Foot and Mouth, changing regulation. We ride out every storm without cutting corners — because when the storm passes, the customer still needs a butcher they can trust." },
  { num: "04", year: "Today", title: "Thirty-five hands, one standard.", body: "Around 35 people work at Tockenham. Many of them have been here more than a decade. In-house Quality Assurance, CCTV throughout, and a modern refrigerated fleet serving the region." },
];

export default function About() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <main className="bg-[color:var(--cream)]">
      <Seo
        title="Heritage — Over a Hundred Years of Wiltshire Butchery"
        description="F. Drury & Sons has been trading over 100 years from Tockenham, Wiltshire. Family-run abattoir and wholesale butcher with full traceability, in-house Quality Assurance and CCTV throughout. Company No. 00610856, incorporated 1958."
        path="/about"
        keywords="Drury history, F Drury and Sons heritage, Wiltshire abattoir history, family butcher Wiltshire, traceability, Quality Assurance, Tockenham"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Heritage", path: "/about" },
        ]}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "Heritage — F. Drury & Sons",
            url: "https://fdruryandsons.co.uk/about",
            isPartOf: { "@id": "https://fdruryandsons.co.uk/#website" },
            about: { "@id": "https://fdruryandsons.co.uk/#localbusiness" },
            inLanguage: "en-GB",
          },
        ]}
      />
      {/* Hero */}
      <section ref={heroRef} className="relative pt-32 md:pt-48 pb-16 md:pb-32 overflow-hidden">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7">
            <MaskReveal><Overline>Heritage · Since c.1924</Overline></MaskReveal>
            <h1 className="mt-6 md:mt-8 font-display tracking-tightest leading-[0.9] text-[16vw] sm:text-[13vw] md:text-[9vw] text-[color:var(--ink)]">
              <MaskReveal delay={0.1}><span>A hundred</span></MaskReveal>
              <MaskReveal delay={0.2}><span className="italic text-[color:var(--oxblood)]">years</span></MaskReveal>
              <MaskReveal delay={0.3}><span>on one yard.</span></MaskReveal>
            </h1>
          </div>
          <motion.div style={{ y }} className="col-span-12 md:col-span-5 md:pt-24 mt-8 md:mt-0">
            <div className="clip-frame grain aspect-[4/5] max-w-md mx-auto md:ml-auto md:mr-0">
              <img
                src="https://images.pexels.com/photos/30488545/pexels-photo-30488545.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Traditional butcher storefront"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline chapters */}
      <section className="relative py-14 md:py-24 border-t border-[color:var(--hairline)] bg-[color:var(--paper)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-12 gap-6 mb-10 md:mb-16">
          <Rise className="col-span-12 md:col-span-4"><Overline>The chapters</Overline></Rise>
          <Rise delay={0.1} className="col-span-12 md:col-span-8">
            <h2 className="mt-3 md:mt-0 font-display text-[10vw] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest">
              How a small village abattoir became <span className="italic text-[color:var(--oxblood)]">a regional trade.</span>
            </h2>
          </Rise>
        </div>

        <div className="mx-auto max-w-[1400px] px-5 md:px-10 space-y-14 md:space-y-32">
          {chapters.map((c, i) => (
            <Rise key={c.num} delay={i * 0.03}>
              <div className="grid grid-cols-12 gap-3 md:gap-12 items-start border-t border-[color:var(--hairline)] pt-8 md:pt-10">
                <div className="col-span-4 md:col-span-2">
                  <div className="chapter-number text-[16vw] sm:text-[12vw] md:text-[7vw] leading-none">{c.num}</div>
                </div>
                <div className="col-span-8 md:col-span-3">
                  <div className="font-ui text-[10px] md:text-xs uppercase tracking-editorial text-[color:var(--ink-light)]">Year</div>
                  <div className="font-display italic text-2xl sm:text-3xl md:text-4xl mt-2 text-[color:var(--ink)]">{c.year}</div>
                </div>
                <div className="col-span-12 md:col-span-7 mt-2 md:mt-0">
                  <h3 className="font-display text-2xl sm:text-3xl md:text-5xl leading-[1.05] tracking-tightest">{c.title}</h3>
                  <p className="mt-4 md:mt-6 font-body text-base md:text-xl leading-relaxed text-[color:var(--ink-light)] max-w-2xl">
                    {c.body}
                  </p>
                </div>
              </div>
            </Rise>
          ))}
        </div>
      </section>

      {/* Traceability / QA */}
      <section className="relative py-16 md:py-40 bg-[color:var(--cream)]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-8 md:gap-16 items-center">
          <div className="col-span-12 md:col-span-5">
            <Rise>
              <Overline>Welfare · Traceability · Craft</Overline>
              <h2 className="mt-4 md:mt-6 font-display text-[10vw] sm:text-5xl md:text-6xl leading-[0.95] tracking-tightest">
                From farm to <span className="italic text-[color:var(--oxblood)]">dispatch.</span>
              </h2>
            </Rise>
            <Rise delay={0.1}>
              <p className="mt-6 md:mt-8 font-body text-base md:text-xl leading-relaxed text-[color:var(--ink-light)]">
                Every animal is tracked from arrival to dispatch — a traceability system we maintain in-house rather than outsource. CCTV runs across all aspects of production and is monitored by a dedicated Quality Assurance team.
              </p>
            </Rise>
            <Rise delay={0.15}>
              <p className="mt-4 md:mt-6 font-body text-base md:text-xl leading-relaxed text-[color:var(--ink-light)]">
                Alongside external audits, we run internal training and refresher programmes: it's the reason a lot of our staff have been here more than a decade — they are trusted to be craftspeople, not just process workers.
              </p>
            </Rise>
          </div>

          <div className="col-span-12 md:col-span-7 grid grid-cols-2 gap-3 md:gap-6">
            <Rise delay={0.05}>
              <div className="clip-frame grain aspect-[3/4]">
                <img src="https://images.pexels.com/photos/31794126/pexels-photo-31794126.jpeg?auto=compress&cs=tinysrgb&w=1000" alt="English countryside cattle" className="w-full h-full object-cover" />
              </div>
            </Rise>
            <Rise delay={0.1}>
              <div className="clip-frame grain aspect-[3/4] md:mt-16">
                <img src="https://images.pexels.com/photos/8314505/pexels-photo-8314505.jpeg?auto=compress&cs=tinysrgb&w=1000" alt="Aerial farm fields" className="w-full h-full object-cover" />
              </div>
            </Rise>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-14 md:py-24 bg-[color:var(--paper)] border-y border-[color:var(--hairline)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
          {[
            { k: "100+", v: "Years of trading" },
            { k: "35", v: "Craftspeople on site" },
            { k: "10+", v: "Years average tenure" },
            { k: "24/7", v: "CCTV & QA monitoring" },
          ].map((s, i) => (
            <Rise key={s.v} delay={i * 0.05}>
              <div>
                <div className="font-display italic text-5xl sm:text-6xl md:text-8xl leading-none text-[color:var(--oxblood)]">{s.k}</div>
                <div className="mt-3 md:mt-4 font-ui text-[10px] md:text-xs uppercase tracking-editorial text-[color:var(--ink-light)]">{s.v}</div>
              </div>
            </Rise>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-32 bg-[color:var(--cream)]">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 text-center">
          <Rise>
            <Overline>Come and see the yard</Overline>
            <h2 className="mt-4 md:mt-6 font-display text-[11vw] sm:text-5xl md:text-6xl leading-[0.95] tracking-tightest">
              You're always welcome<br/> at the <span className="italic text-[color:var(--oxblood)]">counter.</span>
            </h2>
          </Rise>
          <Rise delay={0.1}>
            <div className="mt-8 md:mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/wholesale" className="btn-sharp">Wholesale enquiries</Link>
              <Link to="/contact" className="btn-sharp-outline">Get in touch</Link>
            </div>
          </Rise>
        </div>
      </section>
    </main>
  );
}
